import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { property, state } from 'lit/decorators.js'
import { html } from 'lit-element'

import { DdsTabSelectEvent } from '@/dap-design-system'
import { DdsElement } from '@/internal/dds-hu-element'

import styles from './tab.scss?inline'

/**
 * `dap-ds-tab`
 * @summary A tab item component. It is used in dap-ds-tab-group component slot to create a tab item.
 * @element dap-ds-tab
 * @title - Tab
 * *
 * @slot - The tab title template.
 * @slot content - The tab content.
 *
 * @csspart base - The main tab content container.
 */
export default class DapDSTab extends DdsElement {
  static tagName = 'dap-ds-tab'

  static readonly styles = unsafeCSS(styles)

  /** The tab id. Required */
  @property({ type: String, reflect: true }) tabId: string
  /** The disabled state of the tab. */
  @property({ type: Boolean, reflect: true }) disabled = false

  @state() private _selected: boolean = false

  firstUpdated() {
    this.setAttribute('id', this.tabId)

    const parent = this.parentElement as
      | (HTMLElement & { selectedTabId?: string })
      | null

    parent?.addEventListener('dds-tab-select', (e: DdsTabSelectEvent) => {
      if (e.detail && this.tabId === e.detail.tabId) {
        this._selected = true
      } else if (this._selected) {
        this._selected = false
      }
    })

    // The group's initial `dds-tab-select` event can be emitted before this
    // listener is registered (e.g. when `selectedTabId` is set in markup), so
    // reconcile the selected state from the group's current selection here.
    if (parent?.selectedTabId === this.tabId) {
      this._selected = true
    }
  }

  render() {
    return html`<div
      id=${this.tabId}
      part="base"
      tabindex=${this._selected ? 0 : -1}
      class=${clsx('tab-panel', {
        'tab-panel--selected': this._selected,
      })}
      role="tabpanel"
      aria-labelledby=${this.tabId}>
      <slot class="tab-panel__title"></slot>
      <slot name="content"></slot>
    </div>`
  }
}
